import { __decorate } from "tslib";
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import HelixElement from '../../shared/types/helix-element.js';
import { localized, msg } from '@lit/localize';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { tabGroupStyles } from './tab-group.styles.js';
import { provide } from '@lit/context';
import { activeTabNameContext } from './tab-group-context.js';
import { HlxTab } from './tab.js';
import { createRef, ref } from 'lit-html/directives/ref.js';
import Swiper from 'swiper';
import '../icon/index.js';
import { Keyboard, Manipulation, Mousewheel, Navigation } from 'swiper/modules';
import { watch } from '../../shared/directives/watch.decorator.js';
let HlxTabGroup = class HlxTabGroup extends HelixElement {
    constructor() {
        super(...arguments);
        this.swiperInitialized = false;
        this.tabHeaderRef = createRef();
        this.navSlotRef = createRef();
        this.scrollLeftBtnRef = createRef();
        this.scrollRightBtnRef = createRef();
        this.tabNavResizeObserver = new ResizeObserver(this.updateTabHeaderUnderline.bind(this));
    }
    render() {
        return html `
            <div class="hlx-tab-group-header">
                <div
                    class=${classMap({
            'hlx-tab-container': true,
            swiper: true,
        })}
                    role="tablist"
                    ${ref(this.tabHeaderRef)}
                    @scroll=${() => {
            // Browsers scroll this element when a tab outside the viewport is focused,
            // which messes up swiper animation.
            // It is also unnecessary, since we manually make focused tabs visible via swiper.
            // So, we reset the scroll if it happens during animation:
            if (this.swiper.animating) {
                this.tabHeaderRef.value.scrollTo({ left: 0 });
            }
        }}>
                    <button
                        class=${classMap({
            'hlx-tab-group-nav': true,
            'hlx-swiper-initialized': this.swiperInitialized,
            'hlx-tab-group-nav-prev': true,
        })}
                        ${ref(this.scrollLeftBtnRef)}
                        aria-label=${msg('Show previous tab')}>
                        <hlx-icon name="chevron-left" decorative></hlx-icon>
                    </button>
                    <slot
                        class=${classMap({
            'swiper-wrapper': true,
            'hlx-animation-with-prefers-reduced-motion': true,
            'hlx-tab-container-slot': true,
        })}
                        name="nav"
                        @hlx-tab-click=${this.handleTabClick}
                        @hlx-tab-focus=${this.handleTabFocus}
                        @slotchange=${this.initializeSlides}
                        ${ref(this.navSlotRef)}></slot>
                    <button
                        class=${classMap({
            'hlx-tab-group-nav': true,
            'hlx-animation-with-prefers-reduced-motion': true,
            'hlx-swiper-initialized': this.swiperInitialized,
            'hlx-tab-group-nav-next': true,
        })}
                        ${ref(this.scrollRightBtnRef)}
                        aria-label=${msg('Show next tab')}>
                        <hlx-icon name="chevron-right" decorative></hlx-icon>
                    </button>
                </div>
            </div>
            <div class="hlx-tab-panel-container">
                <slot></slot>
            </div>
        `;
    }
    handleActiveTabNameChange() {
        if (!this.getCurrentTabs().length) {
            return;
        }
        let newActiveTab = this.getActiveTab();
        if (!newActiveTab) {
            this.activeTabName = this.getCurrentTabs()[0].name;
            newActiveTab = this.getActiveTab();
        }
        this.tabNavResizeObserver.disconnect();
        // observe the new active tab for resize (e.g. when fonts load initially)
        this.tabNavResizeObserver.observe(newActiveTab);
        this.scrollToActiveTab();
        this.updateTabHeaderUnderline();
        this.emit('hlx-active-tab-change', { name: this.activeTabName });
    }
    updateTabHeaderUnderline() {
        const openedTab = this.getActiveTab();
        if (!openedTab || !this.tabHeaderRef.value) {
            return;
        }
        const offsetLeft = openedTab.getBoundingClientRect().x - this.navSlotRef.value.getBoundingClientRect().x;
        this.tabHeaderRef.value.style.setProperty('--hlx-tab-current-width', `${openedTab.clientWidth}px`);
        this.tabHeaderRef.value.style.setProperty('--hlx-tab-current-offset', `${offsetLeft}px`);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.tabNavResizeObserver.disconnect();
    }
    firstUpdated() {
        this.swiper = new Swiper(this.tabHeaderRef.value, {
            modules: [Keyboard, Manipulation, Mousewheel, Navigation],
            slidesPerView: 'auto',
            grabCursor: true,
            keyboard: {
                enabled: false,
            },
            mousewheel: {
                enabled: true,
                forceToAxis: true,
            },
            navigation: {
                enabled: true,
                prevEl: this.scrollLeftBtnRef.value,
                nextEl: this.scrollRightBtnRef.value,
            },
            simulateTouch: true,
            slideClass: 'swiper-slide',
            speed: 400,
            wrapperClass: 'swiper-wrapper',
            on: {
                slideChange: (swiper) => {
                    // slidesOffsetBefore is not always set in time,
                    // so we call slideReset() after updateSlides():
                    if (swiper.activeIndex === 0) {
                        swiper.params.slidesOffsetBefore = 0;
                        swiper.updateSlides();
                        swiper.slideReset();
                    }
                    if (swiper.activeIndex > 0 && swiper.params.slidesOffsetBefore === 0) {
                        swiper.params.slidesOffsetBefore = this.scrollLeftBtnRef.value.offsetWidth;
                        swiper.updateSlides();
                        swiper.slideReset();
                    }
                },
                slidesUpdated: () => {
                    const isFirstTimeSlidesUpdated = !this.swiperInitialized;
                    this.swiperInitialized = true;
                    if (isFirstTimeSlidesUpdated) {
                        this.scrollToActiveTab();
                    }
                },
            },
        });
        this.swiper.slideNext = this.slideToNextSlideOnTheRight.bind(this);
        this.swiper.slidePrev = this.slideToPrevSlideOnTheLeft.bind(this);
    }
    initializeSlides() {
        const tabs = this.getCurrentTabs();
        for (const tab of tabs) {
            tab.classList.add('swiper-slide');
        }
        this.swiper.updateSlides();
        this.handleActiveTabNameChange();
    }
    handleTabClick(e) {
        e.stopPropagation();
        this.activeTabName = e.detail.name;
    }
    scrollToActiveTab() {
        const newActiveTab = this.getActiveTab();
        if (!this.isTabFullyVisible(newActiveTab) &&
            this.swiper.activeIndex !== this.swiper.slides.indexOf(newActiveTab)) {
            this.addLeftOffsetIfMovingFromFirstTab();
            this.swiper.slideTo(this.swiper.slides.indexOf(newActiveTab));
        }
    }
    handleTabFocus(e) {
        e.stopPropagation();
        const currentTabs = this.getCurrentTabs();
        const focusableTabs = currentTabs.filter((tab) => !tab.disabled);
        if (focusableTabs.length === 0 || !(e.target instanceof HlxTab)) {
            return;
        }
        const directionToDelta = {
            prev: -1,
            next: 1,
            none: 0,
        };
        const nextIndex = focusableTabs.indexOf(e.target) + (directionToDelta[e.detail.direction] ?? 0);
        let tabToFocus;
        if (nextIndex < 0) {
            tabToFocus = focusableTabs[focusableTabs.length - 1];
        }
        else if (nextIndex >= focusableTabs.length) {
            tabToFocus = focusableTabs[0];
        }
        else {
            tabToFocus = focusableTabs[nextIndex];
        }
        tabToFocus.focus({ preventScroll: true });
        if (!this.isTabFullyVisible(tabToFocus)) {
            this.addLeftOffsetIfMovingFromFirstTab();
            this.swiper.slideTo(currentTabs.indexOf(tabToFocus));
        }
    }
    slideToNextSlideOnTheRight(speed, runCallbacks) {
        this.addLeftOffsetIfMovingFromFirstTab();
        const translate = this.swiper.slides
            .map((elem) => this.calculateSwiperTranslateToAlignRight(elem))
            .filter((slideTranslation) => slideTranslation < this.swiper.translate)[0];
        if (translate === undefined) {
            return;
        }
        this.swiper.translateTo(translate, speed, runCallbacks);
        // set activeIndex to the first visible tab after translate:
        this.swiper.activeIndex = this.swiper.slides.findIndex((slide) => this.calculateSwiperTranslateToAlignLeft(slide) <= translate);
        this.swiper.emit('slideChange');
        return true;
    }
    slideToPrevSlideOnTheLeft(speed, runCallbacks) {
        // The default implementation of 'slidePrev' in swiper only works
        // if the current translate value matches some slide's offsetLeft.
        // In our case, it doesn't always work, since we set translate manually when sliding to the right.
        // So, we just slide to previous tab manually:
        return this.swiper.slideTo(this.swiper.activeIndex - 1, speed, runCallbacks);
    }
    calculateSwiperTranslateToAlignRight(tab) {
        const calculatedTranslate = this.swiper.wrapperEl.offsetWidth -
            (tab.offsetLeft - tab.parentElement.offsetLeft) -
            tab.offsetWidth -
            this.scrollRightBtnRef.value.offsetWidth;
        const maxTranslate = -this.swiper.snapGrid[this.swiper.snapGrid.length - 1];
        if (Number.isNaN(maxTranslate)) {
            return calculatedTranslate;
        }
        // translate is a negative number, so we use 'max' to bound it by max value:
        return Math.max(maxTranslate, calculatedTranslate);
    }
    calculateSwiperTranslateToAlignLeft(tab) {
        let minTranslate = -this.swiper.snapGrid[0];
        const calculatedTranslate = this.scrollLeftBtnRef.value.offsetWidth - tab.offsetLeft;
        if (Number.isNaN(minTranslate)) {
            minTranslate = 0;
        }
        // translate is a negative number, so we use 'min' to bound it by min value:
        return Math.min(minTranslate, calculatedTranslate);
    }
    isTabFullyVisible(tab) {
        const leftBoundary = this.calculateSwiperTranslateToAlignLeft(tab);
        const rightBoundary = this.calculateSwiperTranslateToAlignRight(tab);
        const currentTranslate = Number.isNaN(this.swiper.translate) ? 0 : this.swiper.translate;
        return currentTranslate >= leftBoundary && currentTranslate <= rightBoundary;
    }
    addLeftOffsetIfMovingFromFirstTab() {
        if (this.swiper.activeIndex === 0 && this.swiper.params.slidesOffsetBefore === 0) {
            this.swiper.params.slidesOffsetBefore = this.scrollLeftBtnRef.value.offsetWidth;
            this.swiper.updateSlides();
        }
    }
    getCurrentTabs() {
        return this.navSlotRef.value
            ?.assignedElements()
            .filter((tab) => tab instanceof HlxTab);
    }
    getActiveTab() {
        return this.getCurrentTabs().find((tab) => tab.name === this.activeTabName);
    }
};
HlxTabGroup.styles = [componentStyles, tabGroupStyles];
__decorate([
    provide({ context: activeTabNameContext }),
    property({ type: String, reflect: true, attribute: 'active-tab-name' })
], HlxTabGroup.prototype, "activeTabName", void 0);
__decorate([
    state()
], HlxTabGroup.prototype, "swiperInitialized", void 0);
__decorate([
    watch('activeTabName', { waitUntilFirstUpdate: true })
], HlxTabGroup.prototype, "handleActiveTabNameChange", null);
HlxTabGroup = __decorate([
    customElement('hlx-tab-group'),
    localized()
], HlxTabGroup);
export { HlxTabGroup };
//# sourceMappingURL=tab-group.js.map